import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StaggerItem } from '@/shared/ui/animations';
import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { EmptyState } from '@/shared/ui/EmptyState';

import {
  AnalysisSectionLayout,
  PROSE_CARD_STAGGER_SLOTS,
  SectionSeparator,
} from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import { formatMonth } from '../../utils/formatMonth';
import { brandColor } from '../../utils/brandColor';
import { AiOverview } from './components/AiOverview';
import { BrandFilters } from './components/BrandFilters';
import { MetricCards } from './components/MetricCards';
import { MixTable } from './components/MixTable';
import { SpendByDimension } from './components/SpendByDimension';
import { SpendTrend } from './components/SpendTrend';
import type { MediaLandscapeBodyProps } from './MediaLandscape.types';
import { competitorDomains, resolveAdvertiserDomain } from '@/shared/utils/brandDomains';
import { displayDimensions, type DimensionKind } from './model/dimensions';
import { availableMonths, type MediaLandscapeFilters } from './model/filters';
import type { MediaMetric } from './model/aggregate';
import { buildMixTable, type MixMode } from './model/mixTable';
import { useMediaLandscapeData } from './model/useMediaLandscapeData';
import { parsePathmaticsData } from './model/pathmaticsData.schema';

// This page has no leading highlight card, but the offset keeps its cascade on the
// same rhythm as sections that have one.
const CARD_SETTLE_GAP = 1;
const sectionStagger = (order: number) => PROSE_CARD_STAGGER_SLOTS + CARD_SETTLE_GAP + 1 + order;

const MediaLandscapeBody = ({ data, competitors, exporting }: MediaLandscapeBodyProps) => {
  const { t } = useTranslation('analysis');
  const months = useMemo(() => availableMonths(data.mediaLandscape.monthly), [data]);
  const [filters, setFilters] = useState<MediaLandscapeFilters>(() => ({
    from: months[0] ?? '',
    to: months[months.length - 1] ?? '',
    advertisers: [],
  }));
  const [applyCount, setApplyCount] = useState(0);
  const [metric, setMetric] = useState<MediaMetric>('spend');
  const [barsKind, setBarsKind] = useState<DimensionKind>('channels');
  const [tableKind, setTableKind] = useState<DimensionKind>('channels');
  const [mode, setMode] = useState<MixMode>('brand');
  const view = useMediaLandscapeData(data, filters, metric);

  const tableRows = view[tableKind].advertiser;
  const tableMarketRows = view[tableKind].market;
  // The market row is rendered here, so its dimensions decide the Other column too: one the
  // selection never spends in would otherwise have no column yet still count in its total.
  const tableColumns = useMemo(
    () => displayDimensions(tableRows, tableMarketRows),
    [tableRows, tableMarketRows],
  );
  const mixView = useMemo(
    () =>
      buildMixTable(
        tableRows,
        tableMarketRows,
        view.selectedAdvertisers,
        tableColumns,
        metric,
        mode,
      ),
    [tableRows, tableMarketRows, view.selectedAdvertisers, tableColumns, metric, mode],
  );

  const domains = useMemo(() => competitorDomains(competitors), [competitors]);
  const brands = useMemo(
    () =>
      view.orderedAdvertisers.map((name) => ({
        name,
        color: brandColor(name, view.orderedAdvertisers),
        domain: resolveAdvertiserDomain(name, domains),
      })),
    [view.orderedAdvertisers, domains],
  );

  const scope =
    view.selectedAdvertisers.length === view.orderedAdvertisers.length
      ? t('analysisResult.mediaLandscape.allBrands')
      : t('analysisResult.mediaLandscape.brandCount', {
          selected: view.selectedAdvertisers.length,
          total: view.orderedAdvertisers.length,
        });
  const range =
    filters.from === filters.to
      ? formatMonth(filters.from, 'numeric')
      : `${formatMonth(filters.from, 'numeric')} – ${formatMonth(filters.to, 'numeric')}`;

  const hasInsights = Boolean(data.mediaLandscape.insights);
  const insightsMeta = data.mediaLandscape.insights
    ? t('analysisResult.mediaLandscape.overviewMeta', {
        count: data.mediaLandscape.insights.analyzedAdvertisers.length,
        generated: formatMonth(data.mediaLandscape.insights.generatedAt.slice(0, 7), 'numeric'),
      })
    : '';

  return (
    <div className="flex flex-col gap-10 pb-4">
      {!exporting && (
        <StaggerItem index={sectionStagger(0)} variant="swipeUp">
          <BrandFilters
            months={view.months}
            brands={brands}
            value={filters}
            applyCount={applyCount}
            onApply={(next) => {
              setApplyCount((count) => count + 1);
              setFilters(next);
            }}
            onClear={() => {
              setApplyCount((count) => count + 1);
              setFilters({
                from: months[0] ?? '',
                to: months[months.length - 1] ?? '',
                advertisers: [],
              });
            }}
          />
        </StaggerItem>
      )}
      <SectionSeparator />
      <StaggerItem index={sectionStagger(1)} variant="swipeUp">
        <MetricCards kpis={view.kpis} meta={`${scope} · ${range}`} />
      </StaggerItem>
      {hasInsights && (
        <StaggerItem index={sectionStagger(2)} variant="swipeUp">
          <AiOverview insights={data.mediaLandscape.insights} meta={insightsMeta} />
        </StaggerItem>
      )}
      {hasInsights && <SectionSeparator />}
      {view.hasRows ? (
        <>
          <StaggerItem index={sectionStagger(3)} variant="swipeUp">
            <SpendTrend
              points={view.trend}
              advertisers={view.selectedAdvertisers}
              orderedAdvertisers={view.orderedAdvertisers}
              metric={metric}
              onMetricChange={setMetric}
              insights={view.trendInsights}
            />
          </StaggerItem>
          <SectionSeparator />
          <StaggerItem index={sectionStagger(4)} variant="swipeUp">
            <SpendByDimension
              rows={view[barsKind].advertiser}
              advertisers={view.selectedAdvertisers}
              metric={metric}
              onMetricChange={setMetric}
              kind={barsKind}
              onKindChange={setBarsKind}
              insights={view[barsKind].overIndex}
            />
          </StaggerItem>
          <SectionSeparator />
          <StaggerItem index={sectionStagger(5)} variant="swipeUp">
            <MixTable
              view={mixView}
              columns={tableColumns}
              metric={metric}
              onMetricChange={setMetric}
              kind={tableKind}
              onKindChange={setTableKind}
              mode={mode}
              onModeChange={setMode}
              selectedCount={view.selectedAdvertisers.length}
              totalCount={view.orderedAdvertisers.length}
            />
          </StaggerItem>
        </>
      ) : (
        <EmptyState
          title={t('analysisResult.mediaLandscape.noDataForFilters')}
          description={t('analysisResult.mediaLandscape.noDataForFiltersDescription')}
          height="100%"
        />
      )}
    </div>
  );
};

export const MediaLandscape = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const analysis = useAnalysisFromContext(analysisProp);
  const parsed = useMemo(
    () => parsePathmaticsData(analysis.pathmaticsData),
    [analysis.pathmaticsData],
  );

  // An upload whose dates never parsed still persists a schema-valid blob with no monthly
  // rows. To the user that is indistinguishable from no upload, so it gets the same state
  // rather than an unfilterable empty range.
  const hasNoData =
    parsed.status === 'absent' ||
    (parsed.status === 'ok' && parsed.data.mediaLandscape.monthly.length === 0);

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_MEDIA_LANDSCAPE}
      title={t('analysisResult.mediaLandscape.mediaLandscape')}
      subtitle={t('analysisResult.mediaLandscape.subtitle')}
      exporting={exporting}
    >
      {hasNoData && (
        <EmptyState
          title={t('analysisResult.mediaLandscape.noPathmaticsData')}
          description={t('analysisResult.mediaLandscape.noPathmaticsDataDescription')}
          height="100%"
        />
      )}
      {parsed.status === 'invalid' && (
        <EmptyState
          title={t('analysisResult.mediaLandscape.unreadableData')}
          description={t('analysisResult.mediaLandscape.unreadableDataDescription')}
          height="100%"
        />
      )}
      {parsed.status === 'ok' && !hasNoData && (
        <MediaLandscapeBody
          key={analysis.id}
          data={parsed.data}
          exporting={exporting}
          competitors={analysis.structuredData?.categoryCompetition?.topCompetitors}
        />
      )}
    </AnalysisSectionLayout>
  );
};
